import { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

type ProfileMenuOption = {
  value: "logout";
  label: string;
  icon: string;
};

type UserProfileMenuProps = {
  displayName: string;
  initials: string;
  /** Avatar button instead of a text field. Used on narrow screens. */
  presentation?: "field" | "button";
};

export default function UserProfileMenu({
  displayName,
  initials,
  presentation = "field",
}: UserProfileMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const options: ProfileMenuOption[] = [
    { value: "logout", label: t("common.logout"), icon: "power_settings_new" },
  ];

  if (presentation === "button") {
    return (
      <>
        <IconButton
          aria-label={displayName}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: "primary.main",
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <Typography variant="body2" fontWeight={700} sx={{ px: 2, pt: 1.5, pb: 1 }}>
            {displayName}
          </Typography>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                setMenuAnchor(null);
                if (option.value === "logout") {
                  navigate("/logout");
                }
              }}
              sx={{ minHeight: 48, gap: 1.5, px: 2 }}
            >
              <Icon fontSize="small" color="action">
                {option.icon}
              </Icon>
              <Typography variant="body2">{option.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Autocomplete
      options={options}
      value={null}
      inputValue={displayName}
      onInputChange={() => undefined}
      openOnFocus
      blurOnSelect
      clearOnBlur={false}
      clearOnEscape={false}
      selectOnFocus={false}
      popupIcon={<Icon sx={{ fontSize: 20 }}>expand_more</Icon>}
      filterOptions={(items) => items}
      size="small"
      sx={{ minWidth: { xs: 52, sm: 196 } }}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, next) => option.value === next.value}
      onChange={(_event, next) => {
        if (next?.value === "logout") {
          navigate("/logout");
        }
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Icon fontSize="small" color="action">
              {option.icon}
            </Icon>
            <Typography variant="body2">{option.label}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          aria-label={displayName}
          InputProps={{
            ...params.InputProps,
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.7rem",
                    bgcolor: "primary.main",
                  }}
                >
                  {initials}
                </Avatar>
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              pr: 0.5,
              cursor: "pointer",
            },
            "& .MuiInputBase-input": {
              cursor: "pointer",
              display: { xs: "none", sm: "block" },
            },
          }}
        />
      )}
    />
  );
}
